// Global error boundary: catches uncaught render errors and shows a friendly
// recovery page instead of a blank screen.
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface State {
  error: Error | null;
}

interface Props {
  children: ReactNode;
}

function ErrorPage({ error, onReset }: { error: Error; onReset: () => void }) {
  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        padding: '0 40px',
        textAlign: 'center',
        background: 'var(--ss-bg, #eceff5)',
        color: 'var(--ss-text, #1a1b20)',
        fontFamily: 'var(--ss-sans, system-ui, sans-serif)',
      }}
    >
      <h1
        style={{
          fontSize: 28,
          fontWeight: 600,
          letterSpacing: '-0.01em',
        }}
      >
        Something went wrong
      </h1>
      <p
        style={{
          fontSize: 15,
          color: 'var(--ss-text-2, #565863)',
          maxWidth: 420,
          lineHeight: 1.6,
        }}
      >
        An unexpected error occurred. Reloading usually resolves it - if you were mid-edit, check your last change actually saved before continuing.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '10px 24px',
          borderRadius: 10,
          border: '1px solid var(--ss-accent-border, rgba(100,68,214,0.22))',
          background: 'var(--ss-accent-soft, rgba(100,68,214,0.1))',
          color: 'var(--ss-accent, #6444d6)',
          fontFamily: 'inherit',
          fontWeight: 500,
          fontSize: 14,
          cursor: 'pointer',
        }}
      >
        Reload
      </button>
      <details
        style={{
          marginTop: 8,
          fontSize: 12,
          color: 'var(--ss-text-3, #8c8d97)',
          maxWidth: 560,
          textAlign: 'left',
        }}
      >
        <summary style={{ cursor: 'pointer', marginBottom: 6 }}>Error details</summary>
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            background: 'var(--ss-code-bg, #17181b)',
            color: 'var(--ss-code-text, #e9ecf0)',
            border: '1px solid var(--ss-code-border, rgba(255,255,255,0.08))',
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 11,
          }}
        >
          {error.message}
          {error.stack ? '\n\n' + error.stack : ''}
        </pre>
      </details>
      <button
        onClick={onReset}
        style={{
          fontSize: 12,
          color: 'var(--ss-text-3, #8c8d97)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
          textDecoration: 'underline',
        }}
      >
        Try to recover without reloading
      </button>
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <ErrorPage
          error={this.state.error}
          onReset={() => this.setState({ error: null })}
        />
      );
    }
    return this.props.children;
  }
}
